import { IReviewNew } from '../../../shared/interfaces/IReviewNew';
import React, { FC, useState } from 'react';
import ReviewHeader from '../ReviewHeader';
import ReviewWrapper from '../ReviewWrapper';
import ReviewContent from '../ReviewContent';
import { Box, Button, Divider, Typography, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import Block from '../../Block/Block';
import { ExpandLess, MoreVert } from '@mui/icons-material';
import FeedbackComment from '../../FeedbackComment/FeedbackComment';
import Helper from '../../../shared/helpers/Helper';
import { CommunityManagement } from '../../../shared/interfaces/ICommunityManagement';
import ReviewFooter from '../ReviewFooter';

interface Props extends IReviewNew {
  communityManagement?: CommunityManagement;
  commentingModule: boolean;
  hideHeader: boolean;
  hideFooter: boolean;
}

const StepStoneReview: FC<Props> = ({
  id,
  review,
  platform,
  communityManagement,
  commentingModule,
  reviewComment,
  note,
  deletion,
  deleted,
  timestamp,
  hideHeader,
  hideFooter,
  history,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showAllBlocks, setShowAllBlocks] = useState(false);

  const {
    header,
    jobTitle,
    discipline,
    subDiscipline,
    location,
    employmentStatus,
    employmentDate,
    rating,
    feedbackOnStrengths,
    feedbackOnThingsToImprove,
    companyComment,
    blocks,
  } = review;

  const isAnyNonNull = [
    jobTitle,
    discipline,
    subDiscipline,
    location,
    employmentStatus,
    employmentDate,
  ].some((value) => value);

  return (
    <ReviewWrapper>
      <ReviewHeader
        hide={hideHeader}
        rating={rating}
        platform={platform}
        timestamp={timestamp}
        note={note}
        id={id}
        deletion={deletion}
        commentingModule={commentingModule}
        deleted={deleted}
      />
      <ReviewContent>
        {isAnyNonNull && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mb: theme.spacing(2),
            }}
          >
            {(employmentStatus || employmentDate) && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  cursor: 'pointer',
                }}
              >
                <Trans t={t}>{employmentStatus}</Trans>{' '}
                {employmentDate && `(${Helper.getFormattedDate(employmentDate, true)})`}
              </Typography>
            )}
            {(jobTitle || discipline || subDiscipline) && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  cursor: 'pointer',
                }}
              >
                {jobTitle} {discipline} {subDiscipline}
              </Typography>
            )}
            {location && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  cursor: 'pointer',
                }}
              >
                {location}
              </Typography>
            )}
          </Box>
        )}
        {header && <Typography variant="h6">{header}</Typography>}
        {(feedbackOnStrengths || feedbackOnThingsToImprove) && (
          <Box
            sx={{
              display: 'flex',
              columnGap: theme.spacing(2),
              my: theme.spacing(2),
            }}
          >
            {feedbackOnStrengths && (
              <FeedbackComment
                input={{ type: 'feedbackOnStrengths', comment: feedbackOnStrengths }}
              />
            )}
            {feedbackOnThingsToImprove && (
              <FeedbackComment
                input={{ type: 'feedbackOnThingsToImprove', comment: feedbackOnThingsToImprove }}
              />
            )}
          </Box>
        )}
        {blocks && blocks.length > 0 && (
          <Box sx={{ mt: theme.spacing(3) }}>
            {showAllBlocks ? (
              blocks
                .filter((value) => typeof value.rating === 'number')
                .map(({ rating, text, title }, index) => {
                  return (
                    <Box
                      key={`Review_${id}_Block_${index}`}
                      sx={{ mt: theme.spacing(1) }}
                    >
                      <Divider orientation="horizontal" />
                      <Block
                        rating={rating}
                        text={text}
                        title={title}
                      />
                    </Box>
                  );
                })
            ) : (
              <Box sx={{ mt: theme.spacing(1) }}>
                <Divider orientation="horizontal" />
                <Block
                  rating={blocks[0].rating}
                  text={blocks[0].text}
                  title={blocks[0].title}
                />
              </Box>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={showAllBlocks ? <ExpandLess /> : <MoreVert />}
              onClick={() => setShowAllBlocks(!showAllBlocks)}
              sx={{
                mb: theme.spacing(1),
              }}
            >
              {showAllBlocks ? t('show_less') : t('show_more')}
            </Button>
          </Box>
        )}
      </ReviewContent>
      <ReviewFooter
        platform={platform}
        history={history}
        hide={hideFooter}
        reviewId={id}
        review={review}
        input={header}
        reviewComment={reviewComment}
        communityManagement={communityManagement}
        commentingModule={commentingModule}
        companyComment={companyComment}
      />
    </ReviewWrapper>
  );
};

export default StepStoneReview;
